import {
  useExportDashboards,
  type DashboardsExportRequestFormat,
  type DashboardsExportRequestScope,
} from '@smarthome/api';
import { Download, FileJson, FileCode } from 'lucide-react';
import { useState } from 'react';

import { Button, Modal, RadioGroup, useToast } from '@/components/ui';

export interface ExportAllModalProps {
  isOpen: boolean;
  onClose: () => void;
}

type ExportFormat = DashboardsExportRequestFormat;
type ExportScope = DashboardsExportRequestScope;

const formatOptions = [
  {
    value: 'json' as ExportFormat,
    label: 'JSON',
    description: 'Standard JSON format',
    icon: FileJson,
  },
  {
    value: 'yaml' as ExportFormat,
    label: 'YAML',
    description: 'Human-readable YAML format',
    icon: FileCode,
  },
];

const scopeOptions = [
  {
    value: 'owned' as ExportScope,
    label: 'My dashboards only',
    description: 'Export only dashboards you own',
  },
  {
    value: 'ownedAndShared' as ExportScope,
    label: 'All dashboards',
    description: 'Include dashboards shared with you',
  },
];

export function ExportAllModal({ isOpen, onClose }: ExportAllModalProps) {
  const { addToast } = useToast();
  const [format, setFormat] = useState<ExportFormat>('json');
  const [scope, setScope] = useState<ExportScope>('owned');

  const { mutate: exportDashboards, isPending } = useExportDashboards({
    mutation: {
      onSuccess: (response) => {
        if (response.content && response.filename) {
          // Create and download the file
          const blob = new Blob([response.content], {
            type: format === 'json' ? 'application/json' : 'text/yaml',
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = response.filename;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);

          addToast({ message: 'Dashboards exported successfully', variant: 'success' });
          onClose();
        }
      },
      onError: (err) => {
        addToast({
          message: err instanceof Error ? err.message : 'Failed to export dashboards',
          variant: 'error',
        });
      },
    },
  });

  const handleExport = () => {
    exportDashboards({
      data: {
        format,
        scope,
      },
    });
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Export Dashboards"
      subtitle="Export your dashboards to a file"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleExport} loading={isPending}>
            <Download className="mr-2 h-4 w-4" />
            Export
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Format selection */}
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-zinc-300">
            Format
          </label>
          <div className="grid grid-cols-2 gap-3">
            {formatOptions.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => setFormat(option.value)}
                className={`flex flex-col items-center gap-2 rounded-lg border-2 p-4 transition-colors ${
                  format === option.value
                    ? 'border-blue-500 bg-blue-50 dark:border-blue-400 dark:bg-blue-500/10'
                    : 'border-gray-200 hover:border-gray-300 dark:border-white/[0.08] dark:hover:border-white/[0.15]'
                }`}
              >
                <option.icon
                  className={`h-6 w-6 ${
                    format === option.value
                      ? 'text-blue-600 dark:text-blue-400'
                      : 'text-gray-400 dark:text-zinc-500'
                  }`}
                />
                <span
                  className={`text-sm font-medium ${
                    format === option.value
                      ? 'text-blue-700 dark:text-blue-300'
                      : 'text-gray-700 dark:text-zinc-300'
                  }`}
                >
                  {option.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Scope selection */}
        <RadioGroup
          label="Scope"
          name="export-scope"
          value={scope}
          onChange={(value) => setScope(value as ExportScope)}
          options={scopeOptions}
        />
      </div>
    </Modal>
  );
}
